import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeContext from '../contexts/ThemeContext';
import i18n from '../i18n';
import { isPhoneNumberValid } from '../utils/validateFields';
import { AddCircleIcon, CloseIcon } from './PrebookingIcons';

/**
 * PhoneListManager Component
 * Allows users to add and remove phone numbers with validation
 * Displays phone numbers as removable chips/tags
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PhoneInput = styled.input`
  flex: 1;
  padding: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 1.4;
  height: 40px;
  background-color: #f6f8fb;
  color: rgba(69, 68, 107, 0.6);
  box-sizing: border-box;

  &::placeholder {
    color: rgba(69, 68, 107, 0.6);
  }

  &:focus {
    outline: none;
    border-color: #45446b;
    color: #45446b;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  height: 40px;
  border: 1px solid rgba(69, 68, 107, 0.4);
  background-color: white;
  border-radius: 20px;
  color: #45446b;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.15px;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: #45446b;
    background-color: rgba(69, 68, 107, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const PhoneChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const PhoneChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #f6f8fb;
  border: 1px solid rgba(69, 68, 107, 0.2);
  border-radius: 50px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.15px;
  color: #45446b;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(234, 126, 143, 0.1);
    border-color: rgba(234, 126, 143, 0.5);
  }
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: rgba(69, 68, 107, 0.6);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #EA7E8F;

    svg path {
      stroke: #EA7E8F;
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ErrorText = styled.span`
  color: ${({ colors }) => colors?.coral || '#EA7E8F'};
  font-size: 12px;
  margin-top: 4px;
`;

const PhoneListManager = ({ phoneNumbers, onAdd, onRemove, placeholder }) => {
  const { colors } = useContext(ThemeContext);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const handleAdd = () => {
    // Clear previous errors
    setError('');

    // Trim whitespace
    const phone = inputValue.trim();

    // Validate not empty
    if (!phone) {
      setError(i18n('phone_required') || 'Phone number is required');
      return;
    }

    // Validate phone format
    if (!isPhoneNumberValid(phone)) {
      setError(i18n('invalid_phone_number') || 'Invalid phone number');
      return;
    }

    // Check for duplicates
    if (phoneNumbers.includes(phone)) {
      setError(i18n('phone_already_added') || 'Phone number already added');
      return;
    }

    // Add phone
    onAdd(phone);

    // Clear input
    setInputValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleRemove = (index) => {
    onRemove(index);
    setError('');
  };

  return (
    <Container>
      <InputRow>
        <PhoneInput
          type="tel"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError('');
          }}
          onKeyPress={handleKeyPress}
          placeholder={placeholder || i18n('enter_phone_number') || '+358 40 xxx xxxx'}
        />
        <AddButton onClick={handleAdd} disabled={!inputValue.trim()}>
          <AddCircleIcon color={colors.iconAccentColor} size={16} />
          {i18n('add') || 'Add'}
        </AddButton>
      </InputRow>

      {error && <ErrorText colors={colors}>{error}</ErrorText>}

      {phoneNumbers?.length > 0 && (
        <PhoneChipsContainer>
          {phoneNumbers.map((phone, index) => (
            <PhoneChip key={index}>
              <span>{phone}</span>
              <RemoveButton
                onClick={() => handleRemove(index)}
                title={i18n('remove') || 'Remove'}
              >
                <CloseIcon color="#45446b" size={16} />
              </RemoveButton>
            </PhoneChip>
          ))}
        </PhoneChipsContainer>
      )}
    </Container>
  );
};

PhoneListManager.propTypes = {
  phoneNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default PhoneListManager;
